import { useCallback, useLayoutEffect, useState } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  getStraightPath,
  getBezierPath,
  type EdgeProps,
} from "@xyflow/react";
import { useCanvasEditability, useCanvasEditSession } from "../../lib/canvas-editability";
import { patchEdge, removeElements } from "../../lib/canvas-commands";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@trillion/ui/components/ui/popover";
import {
  ArrowRight,
  ArrowLeft,
  ArrowLeftRight,
  Minus,
  MoreHorizontal,
  Trash2,
} from "lucide-react";

export type LineStyle = "solid" | "dashed" | "dotted";
export type ArrowMode = "none" | "end" | "start" | "both";
export type PathStyle = "smoothstep" | "straight" | "bezier";

export interface CustomEdgeData {
  lineStyle?: LineStyle;
  arrowMode?: ArrowMode;
  pathStyle?: PathStyle;
  label?: string;
  [key: string]: unknown;
}

function getStrokeDasharray(style: LineStyle): string | undefined {
  switch (style) {
    case "dashed":
      return "8 4";
    case "dotted":
      return "2 4";
    default:
      return undefined;
  }
}

function getMarkerStart(mode: ArrowMode): string | undefined {
  return mode === "start" || mode === "both" ? "url(#board-arrow-start)" : undefined;
}

function getMarkerEnd(mode: ArrowMode): string | undefined {
  return mode === "end" || mode === "both" ? "url(#board-arrow-end)" : undefined;
}

export function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  label,
  interactionWidth,
}: EdgeProps) {
  const edgeData = (data ?? {}) as CustomEdgeData;
  const lineStyle: LineStyle = edgeData.lineStyle ?? "solid";
  const arrowMode: ArrowMode = edgeData.arrowMode ?? "end";
  const pathStyle: PathStyle = edgeData.pathStyle ?? "smoothstep";
  const displayLabel = typeof edgeData.label === "string" ? edgeData.label
    : typeof label === "string" || typeof label === "number" ? String(label) : "";

  const { execute, canEditDocument, isDocumentEditable, generation } = useCanvasEditability();
  const edit = useCanvasEditSession("text");
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    setOpen(false);
  }, [canEditDocument, generation]);

  // Compute path based on style
  let edgePath: string;
  let labelX: number;
  let labelY: number;

  if (pathStyle === "straight") {
    [edgePath, labelX, labelY] = getStraightPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
    });
  } else if (pathStyle === "bezier") {
    [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
  } else {
    [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: 8,
    });
  }

  const updateData = useCallback((data: Partial<CustomEdgeData>) => {
    execute(graph => {
      const current = graph.edges.find(edge => edge.id === id)?.data ?? {};
      const defaults: Record<string, unknown> = { lineStyle: "solid", arrowMode: "end", pathStyle: "smoothstep" };
      if (Object.entries(data).every(([key, value]) => value === (current[key] ?? defaults[key]))) return graph;
      return patchEdge(id, { data })(graph);
    });
  }, [id, execute]);
  const handleDelete = useCallback(() => execute(removeElements([], [id])), [id, execute]);
  const commitLabel = (label: string) => {
    if (label !== displayLabel) edit.update(patchEdge(id, { data: { label } }));
    edit.finish();
  };

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        interactionWidth={typeof interactionWidth === "number" && Number.isFinite(interactionWidth) ? Math.max(20, interactionWidth) : 20}
        style={{
          strokeDasharray: getStrokeDasharray(lineStyle),
          strokeWidth: selected ? 2.5 : 2,
          stroke: selected ? "var(--color-primary)" : "var(--color-foreground)",
        }}
        markerStart={getMarkerStart(arrowMode)}
        markerEnd={getMarkerEnd(arrowMode)}
      />

      <EdgeLabelRenderer>
        {/* Clickable label / toolbar trigger */}
        <div
          className="nodrag nopan"
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "all",
          }}
        >
          {selected && canEditDocument ? (
            <Popover open={open && canEditDocument} onOpenChange={(next) => setOpen(next && isDocumentEditable())}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Edit connector"
                  className="h-7 gap-1 rounded-full border-primary bg-background px-2 text-xs shadow-md"
                >
                  <MoreHorizontal className="h-3 w-3" />
                  {displayLabel && <span>{displayLabel}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="nodrag nopan nowheel w-auto p-2"
                side="top"
                align="center"
                sideOffset={8}
              >
                <div className="flex flex-col gap-2">
                  {/* Line style */}
                  <div className="flex items-center gap-1">
                    <span className="mr-1 text-xs text-muted-foreground">Line:</span>
                    {(["solid", "dashed", "dotted"] as LineStyle[]).map((ls) => (
                      <Button
                        key={ls}
                        aria-label={`${ls} line`}
                        variant={lineStyle === ls ? "default" : "ghost"}
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => updateData({ lineStyle: ls })}
                      >
                        <svg width="24" height="12" viewBox="0 0 24 12">
                          <line
                            x1="0"
                            y1="6"
                            x2="24"
                            y2="6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeDasharray={
                              ls === "dashed" ? "6 3" : ls === "dotted" ? "2 3" : undefined
                            }
                          />
                        </svg>
                      </Button>
                    ))}
                  </div>

                  {/* Path style */}
                  <div className="flex items-center gap-1">
                    <span className="mr-1 text-xs text-muted-foreground">Path:</span>
                    {(
                      [
                        { key: "smoothstep", label: "Curved" },
                        { key: "straight", label: "Straight" },
                        { key: "bezier", label: "Bezier" },
                      ] as { key: PathStyle; label: string }[]
                    ).map(({ key, label: lbl }) => (
                      <Button
                        key={key}
                        variant={pathStyle === key ? "default" : "ghost"}
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => updateData({ pathStyle: key })}
                      >
                        {lbl}
                      </Button>
                    ))}
                  </div>

                  {/* Arrow mode */}
                  <div className="flex items-center gap-1">
                    <span className="mr-1 text-xs text-muted-foreground">Arrow:</span>
                    <Button
                      variant={arrowMode === "none" ? "default" : "ghost"}
                      size="sm"
                      className="h-7 w-7 p-0"
                      aria-label="No arrows"
                      title="No arrows"
                      onClick={() => updateData({ arrowMode: "none" })}
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant={arrowMode === "end" ? "default" : "ghost"}
                      size="sm"
                      className="h-7 w-7 p-0"
                      aria-label="Arrow at end"
                      title="Arrow at end"
                      onClick={() => updateData({ arrowMode: "end" })}
                    >
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant={arrowMode === "start" ? "default" : "ghost"}
                      size="sm"
                      className="h-7 w-7 p-0"
                      aria-label="Arrow at start"
                      title="Arrow at start"
                      onClick={() => updateData({ arrowMode: "start" })}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant={arrowMode === "both" ? "default" : "ghost"}
                      size="sm"
                      className="h-7 w-7 p-0"
                      aria-label="Arrows both ends"
                      title="Arrows both ends"
                      onClick={() => updateData({ arrowMode: "both" })}
                    >
                      <ArrowLeftRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Label input */}
                  <div className="flex items-center gap-1">
                    <span className="mr-1 text-xs text-muted-foreground">Label:</span>
                    <input
                      className="nodrag nopan nowheel h-7 flex-1 rounded border bg-transparent px-2 text-xs outline-none focus:border-primary"
                      aria-label="Connector label"
                      placeholder="Edge label..."
                      defaultValue={displayLabel}
                      onFocus={edit.begin}
                      onChange={() => { if (!edit.active) edit.begin(); }}
                      onBlur={(e) => commitLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          commitLabel((e.target as HTMLInputElement).value);
                          e.currentTarget?.blur();
                        }
                      }}
                    />
                  </div>

                  {/* Delete */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 justify-start gap-2 text-xs text-destructive hover:text-destructive"
                    onClick={handleDelete}
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete connector
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          ) : displayLabel ? (
            <span className="rounded bg-background/80 px-1.5 py-0.5 text-xs text-foreground">
              {displayLabel}
            </span>
          ) : null}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
